import { SupportedTpaAppDefIds } from '@wix/a11y-adapters';
import { dsStudioDataModifier } from '@wix/a11y-adapters/studio';
import { useTranslation } from '@wix/fe-essentials/react';
import { assertNotOptional } from '@wix/seo-common-core';
import { useAtomValue } from 'jotai';
import _ from 'lodash';
import { useCallback, useMemo } from 'react';
import { altTextScreenPageIdAtom } from '../../../atoms/session-atoms';
import { useTpaEntityDataHandlers } from '../../../hooks/tpa/use-tpa-entity-data-handlers';
import { useA11yWizardAdapter } from '../../../hooks/use-a11y-wizard-adapter';
import { PanoramaTransactions } from '../../../panorama/constants';
import { usePanoramaFunctions } from '../../../panorama/use-panorama-functions';
import { getCompRefType } from '../../../utils/get-comp-ref-type';
export const useStaticPagesAltTextHandlers = () => {
  const {
    t
  } = useTranslation();
  const {
    a11yWizardAdapter
  } = useA11yWizardAdapter();
  const {
    setTpaEntityData
  } = useTpaEntityDataHandlers();
  const {
    editorHost
  } = useA11yWizardAdapter();
  const altTextScreenPageId = useAtomValue(altTextScreenPageIdAtom);
  assertNotOptional(altTextScreenPageId);
  const {
    startPanoramaTransaction,
    finishPanoramaTransaction
  } = usePanoramaFunctions();
  const openImageElement = useCallback(nextElement => {
    const {
      id,
      breakpointId,
      tpaData
    } = nextElement;
    const nextCompRef = {
      id: (tpaData == null ? void 0 : tpaData.componentId) ?? id,
      type: getCompRefType(editorHost, breakpointId)
    };
    if (nextCompRef) {
      a11yWizardAdapter.selectComponent(nextCompRef, breakpointId);
    }
  }, [a11yWizardAdapter, editorHost]);
  const getEntityName = useCallback(_entityId => {
    return t('accessibility-wizard.verticals-drill-in.static-group-title');
  }, [t]);
  const enabledDecorativeImages = true;
  const setAltText = useCallback(async data => {
    const {
      isDecorative,
      alt,
      id,
      tpaData,
      breakpointId,
      builderLabel
    } = data;
    if (tpaData && tpaData.appDefId) {
      await setTpaEntityData(tpaData.appDefId, [data], true);
      return;
    }
    const compRef = {
      id,
      type: getCompRefType(editorHost, breakpointId)
    };
    const component = a11yWizardAdapter.getComponent(compRef);
    const updatedComponent = dsStudioDataModifier.updateImageProperties(component, {
      alt: isDecorative ? '' : alt,
      builderLabel,
      decorative: isDecorative ?? false
    });
    updatedComponent.design && a11yWizardAdapter.setComponentDesign(compRef, updatedComponent.design);
    updatedComponent.data && a11yWizardAdapter.setComponentData(compRef, updatedComponent.data);
  }, [a11yWizardAdapter, editorHost, setTpaEntityData]);
  const setBulkAltText = useCallback(async altTextData => {
    startPanoramaTransaction(PanoramaTransactions.bulkApplyStaticPageAltImage, {
      altTextData
    });
    const itemsWithoutTpaData = altTextData.filter(item => !item.tpaData);
    const itemsWithTpaData = altTextData.filter(item => item.tpaData);
    const itemsWithTpaDataByAppDefId = _.groupBy(itemsWithTpaData, _ref => {
      let {
        tpaData
      } = _ref;
      return (tpaData == null ? void 0 : tpaData.appDefId) ?? SupportedTpaAppDefIds.PRO_GALLERY;
    });
    itemsWithoutTpaData.forEach(data => setAltText(data));
    await Promise.all(Object.entries(itemsWithTpaDataByAppDefId).map(async _ref2 => {
      let [appDefId, items] = _ref2;
      await setTpaEntityData(appDefId, items, true);
    }));
    finishPanoramaTransaction(PanoramaTransactions.bulkApplyStaticPageAltImage, {
      altTextData
    });
  }, [finishPanoramaTransaction, setAltText, setTpaEntityData, startPanoramaTransaction]);
  return useMemo(() => ({
    enabledDecorativeImages,
    getEntityName,
    openImageElement,
    setBulkAltText
  }), [enabledDecorativeImages, getEntityName, openImageElement, setBulkAltText]);
};
//# sourceMappingURL=use-static-pages-alt-text-handlers.js.map